import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import TextInput from 'components/ReusableComponents/ManageHotel/TextInput'
import { useEmailSubscriptionGate } from 'components/ReusableComponents/UnlockServiceDialog'
import { applyInviteEmailDesignToCampaign } from 'features/EmailEditor/applyInviteEmailDesignToCampaign'
import { buildInviteEmailDesignPreviewHtml } from 'features/InviteEmailDesign/buildInviteEmailDesignPreviewHtml'
import InviteEmailDesignTemplatePreview from 'features/InviteEmailDesign/InviteEmailDesignTemplatePreview'
import { resolveInviteEmailDesignTemplateForScope } from 'features/InviteEmailDesign/inviteEmailDesignTemplateHelpers'
import { invalidateInviteEmailDesignPreviewCache } from 'features/InviteEmailDesign/useInviteEmailDesignPreview'
import useInviteEmailDesignApply from 'features/InviteEmailDesign/useInviteEmailDesignApply'
import { getBulkMediumAffectedCampaigns } from 'features/ManageCampaigns/utils/inviteBulkMediumHelpers'
import useAvailableImageUrl from 'hooks/useAvailableImageUrl'
import useBrand from 'hooks/useBrand'
import campaignApi from 'services/api/campaign'
import inviteEmailDesignApi from 'services/api/inviteEmailDesign'
import { Link } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useToasts } from 'react-toast-notifications'

import styles from './InviteEmailDesign.module.scss'

function resolveApplyConfirmTestCampaign({
  isCampaignMode,
  campaignId,
  campaigns,
  scope
}) {
  if (isCampaignMode) {
    return (
      (campaigns || []).find(
        campaign =>
          String(campaign.contentId) === String(campaignId) ||
          String(campaign.id) === String(campaignId)
      ) || null
    )
  }

  return getBulkMediumAffectedCampaigns(campaigns, scope)[0] || null
}

const InviteEmailDesignApplyTemplateModal = ({
  isOpen,
  scopeRow,
  templates = [],
  campaigns,
  contentSubsidiaryId,
  campaignId,
  relatedCampaignIds = [],
  initialTemplateId,
  campaignDescription = 'This campaign',
  onClose,
  onApplied
}) => {
  const isCampaignMode = Boolean(campaignId)
  const { logoUrl: brandLogoCandidateUrl } = useBrand()
  const brandLogoUrl = useAvailableImageUrl(brandLogoCandidateUrl)
  const { addToast } = useToasts()
  const currentUserEmail = useSelector(state => state.login.currentUser?.email || '')
  const currentUserName = useSelector(state => state.login.currentUser?.name || '')
  const { promptEmailSubscriptionUpgrade } = useEmailSubscriptionGate()
  const { applyTemplateToScope, getAffectedCampaignNames, validateScope } = useInviteEmailDesignApply({
    campaigns,
    contentSubsidiaryId
  })

  const [searchQuery, setSearchQuery] = useState('')
  const [selectedTemplateId, setSelectedTemplateId] = useState(null)
  const [hoveredTemplateId, setHoveredTemplateId] = useState(null)
  const [activeTab, setActiveTab] = useState('templates')
  const [isAffectedListExpanded, setIsAffectedListExpanded] = useState(false)
  const [isApplying, setIsApplying] = useState(false)
  const [previewRefreshKey, setPreviewRefreshKey] = useState(0)
  const [testEmailAddress, setTestEmailAddress] = useState('')
  const [isSendingTestEmail, setIsSendingTestEmail] = useState(false)

  const initialTemplate = useMemo(() => {
    if (isCampaignMode) {
      if (initialTemplateId) {
        return templates.find(template => template.id === initialTemplateId) || null
      }

      return templates.find(template => template.isActive) || templates[0] || null
    }

    if (!scopeRow) {
      return null
    }

    return resolveInviteEmailDesignTemplateForScope(templates, scopeRow.scope)
  }, [initialTemplateId, isCampaignMode, scopeRow, templates])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    setSearchQuery('')
    setSelectedTemplateId(initialTemplate?.id || templates[0]?.id || null)
    setHoveredTemplateId(null)
    setActiveTab('templates')
    setIsAffectedListExpanded(false)
    setIsApplying(false)
    setIsSendingTestEmail(false)
    setTestEmailAddress(currentUserEmail)
    templates.forEach(template => invalidateInviteEmailDesignPreviewCache(template.id))
    setPreviewRefreshKey(current => current + 1)
  }, [currentUserEmail, initialTemplate?.id, isOpen, templates])

  const filteredTemplates = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase()

    if (!normalizedQuery) {
      return templates
    }

    return templates.filter(template => {
      const haystack = `${template.name || ''} ${template.shellTemplateKey || ''}`.toLowerCase()
      return haystack.includes(normalizedQuery)
    })
  }, [searchQuery, templates])

  if (!isOpen || (!scopeRow && !isCampaignMode)) {
    return null
  }

  const selectedTemplate =
    templates.find(template => template.id === selectedTemplateId) || initialTemplate || null
  const previewTemplateId = hoveredTemplateId || selectedTemplateId
  const affectedNames = scopeRow ? getAffectedCampaignNames(scopeRow.scope) : []
  const modalTitle = isCampaignMode
    ? 'Choose email design'
    : `Choose email design — ${scopeRow.title}`
  const modalIntro = isCampaignMode
    ? 'Pick a saved email design for this campaign. This updates the email shell (logo, hero, colors, footer). Message content is not changed.'
    : `Pick a saved email design for ${scopeRow.title.toLowerCase()}. This updates the email shell (logo, hero, colors, footer). Message text on each campaign is not changed.`
  const applyButtonLabel = isCampaignMode
    ? 'Apply design'
    : `Apply to ${scopeRow.title.toLowerCase()}`
  const sidePreviewDescription = isCampaignMode ? campaignDescription : scopeRow?.description

  function handleClose() {
    if (!isApplying) {
      onClose()
    }
  }

  function handleApplyClick() {
    if (!selectedTemplate?.id || isApplying || isSendingTestEmail) {
      return
    }

    if (promptEmailSubscriptionUpgrade()) {
      return
    }

    if (!isCampaignMode && !validateScope(scopeRow.scope)) {
      return
    }

    setIsApplying(true)

    const applyPromise = isCampaignMode
      ? applyInviteEmailDesignToCampaign({
          campaignId,
          templateId: selectedTemplate.id,
          brandLogoUrl,
          relatedCampaignIds
        })
      : applyTemplateToScope({
          templateId: selectedTemplate.id,
          scope: scopeRow.scope
        })

    applyPromise
      .then(() => {
        if (onApplied) {
          onApplied({
            scope: scopeRow?.scope,
            templateId: selectedTemplate.id,
            templateName: selectedTemplate.name
          })
        }
        onClose()
      })
      .catch(() => {})
      .finally(() => {
        setIsApplying(false)
      })
  }

  function toggleAffectedList() {
    setIsAffectedListExpanded(current => !current)
  }

  function handleSendTestEmail() {
    if (!selectedTemplate?.id || isSendingTestEmail || isApplying) {
      return
    }

    const email = (testEmailAddress || '').trim()
    if (!email) {
      addToast('Enter an email address to send a test.', { appearance: 'warning' })
      return
    }

    const testCampaign = resolveApplyConfirmTestCampaign({
      isCampaignMode,
      campaignId,
      campaigns,
      scope: scopeRow?.scope
    })

    if (!testCampaign?.contentId || !testCampaign?.category) {
      addToast('No eligible campaign was found to send a test email.', {
        appearance: 'warning'
      })
      return
    }

    setIsSendingTestEmail(true)

    inviteEmailDesignApi
      .getTemplate({ templateId: selectedTemplate.id })
      .then(emailData => {
        const emailBodyHtml = buildInviteEmailDesignPreviewHtml(
          emailData,
          currentUserName,
          brandLogoUrl
        )

        if (!emailBodyHtml) {
          throw new Error('Design template html was not found')
        }

        return campaignApi.sendTestEmail({
          contentCategory: testCampaign.category,
          contentId: testCampaign.contentId,
          email,
          emailBodyHtml,
          emailSubject:
            emailData.emailParams?.subject ||
            emailData.emailParams?.Subject ||
            selectedTemplate.name ||
            'Test email'
        })
      })
      .then(() => {
        addToast(`Test email for "${selectedTemplate.name}" sent successfully!`, {
          appearance: 'success'
        })
      })
      .catch(() => {
        addToast(`Failed to send test email for "${selectedTemplate.name}".`, {
          appearance: 'error'
        })
      })
      .finally(() => {
        setIsSendingTestEmail(false)
      })
  }

  return (
      <ConfirmationDialog
        title={modalTitle}
        negativeMessage="Cancel"
        positiveMessage={isApplying ? 'Applying…' : applyButtonLabel}
        onClose={handleClose}
        onConfirm={handleApplyClick}
        isButtonDisabled={
          !selectedTemplate?.id || isApplying || isSendingTestEmail || !templates.length
        }
        maxWidth={1200}
        styleOverrides={{
          width: 'min(96vw, 1200px)',
          maxHeight: 'min(90vh, calc(100vh - 60px))',
          overflow: 'hidden'
        }}
        contentPadding="24px"
        contentStyleOverrides={{
          flex: '1 1 auto',
          minHeight: 0,
          maxHeight: 'none',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box'
        }}
      >
        <div className={styles.applyTemplateModalBody}>
          <div className={styles.applyTemplateModalMain}>
            <p className={styles.applyTemplateModalIntro}>{modalIntro}</p>

            <div className={styles.applyTemplateModalTabs} role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'templates'}
                className={
                  activeTab === 'templates'
                    ? styles.applyTemplateModalTabActive
                    : styles.applyTemplateModalTab
                }
                onClick={() => setActiveTab('templates')}
              >
                Templates
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'editTest'}
                className={
                  activeTab === 'editTest'
                    ? styles.applyTemplateModalTabActive
                    : styles.applyTemplateModalTab
                }
                onClick={() => setActiveTab('editTest')}
              >
                Edit & test
              </button>
            </div>

            {activeTab === 'templates' ? (
              <div className={styles.applyTemplateModalTabPanel} role="tabpanel">
                <div className={styles.applyTemplateModalToolbar}>
                  <TextInput
                    label="Search templates"
                    value={searchQuery}
                    onChangeValue={setSearchQuery}
                    placeholder="Search by name..."
                    autoFocus
                  />
                </div>

                {templates.length === 0 ? (
                  <p className={styles.applyTemplateModalEmpty}>
                    No email designs yet.&nbsp;
                    <Link
                      className={styles.noticeLink}
                      to="/marketing-assets/email-templates"
                    >
                      Create one first
                    </Link>
                    .
                  </p>
                ) : (
                  <>
                    <div className={styles.applyTemplateModalGrid}>
                      {filteredTemplates.map(template => {
                        const isSelected = selectedTemplateId === template.id
                        const isHovered = hoveredTemplateId === template.id

                        return (
                          <button
                            key={template.id}
                            type="button"
                            className={[
                              isSelected
                                ? styles.applyTemplateModalCardSelected
                                : styles.applyTemplateModalCard,
                              isHovered ? styles.applyTemplateModalCardHovered : ''
                            ]
                              .filter(Boolean)
                              .join(' ')}
                            onClick={() => setSelectedTemplateId(template.id)}
                            onMouseEnter={() => setHoveredTemplateId(template.id)}
                            onMouseLeave={() => setHoveredTemplateId(null)}
                          >
                            <div className={styles.applyTemplateModalCardPreview}>
                              <InviteEmailDesignTemplatePreview
                                templateId={template.id}
                                variant="card"
                                fitToContainer
                                enabled
                                refreshKey={previewRefreshKey}
                              />
                            </div>
                            <span className={styles.applyTemplateModalCardLabel}>
                              {template.name}
                            </span>
                            {(isCampaignMode
                              ? String(template.id) === String(initialTemplateId)
                              : template.isActive) && (
                              <span className={styles.applyTemplateModalCardBadge}>Active</span>
                            )}
                          </button>
                        )
                      })}
                    </div>
                    {filteredTemplates.length === 0 && (
                      <p className={styles.applyTemplateModalEmpty}>
                        No templates match your search.
                      </p>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div className={styles.applyTemplateModalTabPanel} role="tabpanel">
                {!selectedTemplate ? (
                  <p className={styles.applyTemplateModalEmpty}>
                    Select a template in the Templates tab first.
                  </p>
                ) : (
                  <div className={styles.applyTemplateEditTestPanel}>
                    <section className={styles.applyTemplateEditTestCard}>
                      <div className={styles.applyTemplateEditTestCardHeader}>
                        <div className={styles.applyTemplateEditTestCardCopy}>
                          <span className={styles.applyTemplateEditTestLabel}>Selected layout</span>
                          <span className={styles.applyTemplateEditTestTitle}>
                            {selectedTemplate.name}
                          </span>
                        </div>
                        <Link
                          className={styles.applyTemplateEditTestSecondaryButton}
                          to={`/marketing-assets/email-templates/${selectedTemplate.id}`}
                        >
                          Edit design
                        </Link>
                      </div>
                      <p className={styles.applyTemplateEditTestHint}>
                        Open the editor to change logo, hero, colors, buttons, or footer. Message
                        text on this campaign is not changed when you apply.
                      </p>
                    </section>

                    {!isCampaignMode && affectedNames.length > 0 && (
                      <section className={styles.applyTemplateEditTestCard}>
                        <span className={styles.applyTemplateEditTestLabel}>Campaigns</span>
                        <div className={styles.applyTemplateAffectedCampaigns}>
                          <button
                            type="button"
                            className={styles.applyTemplateAffectedToggle}
                            aria-expanded={isAffectedListExpanded}
                            onClick={toggleAffectedList}
                          >
                            <span>
                              {scopeRow.title} ({affectedNames.length})
                            </span>
                            <span
                              className={[
                                styles.applyTemplateAffectedChevron,
                                isAffectedListExpanded
                                  ? styles.applyTemplateAffectedChevronOpen
                                  : ''
                              ]
                                .filter(Boolean)
                                .join(' ')}
                              aria-hidden="true"
                            />
                          </button>
                          {isAffectedListExpanded && (
                            <ul className={styles.confirmList}>
                              {affectedNames.map(name => (
                                <li key={name}>{name}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </section>
                    )}

                    <section className={styles.applyTemplateEditTestCard}>
                      <span className={styles.applyTemplateEditTestLabel}>Send test email</span>
                      <p className={styles.applyTemplateEditTestHint}>
                        Send a test before applying. Guests will receive the same images, buttons,
                        and text you see in your inbox.
                      </p>
                      <div className={styles.applyTemplateEditTestForm}>
                        <TextInput
                          type="email"
                          className={styles.applyTemplateEditTestEmailInput}
                          value={testEmailAddress}
                          onChangeValue={setTestEmailAddress}
                          placeholder="Email address"
                        />
                        <button
                          type="button"
                          className={styles.applyTemplateEditTestPrimaryButton}
                          onClick={handleSendTestEmail}
                          disabled={isSendingTestEmail || isApplying}
                        >
                          {isSendingTestEmail ? 'Sending…' : 'Send Test Email'}
                        </button>
                      </div>
                    </section>
                  </div>
                )}
              </div>
            )}
          </div>
          <aside className={styles.applyTemplateSidePreview}>
            <div className={styles.applyTemplateSidePreviewHeader}>
              <span className={styles.applyTemplateSidePreviewName}>
                {(
                  templates.find(template => template.id === previewTemplateId) || selectedTemplate
                )?.name || 'Preview'}
              </span>
              <span className={styles.applyTemplateSidePreviewDescription}>
                {sidePreviewDescription}
              </span>
            </div>
            <div className={styles.applyTemplateSidePreviewViewport}>
              {previewTemplateId ? (
                <InviteEmailDesignTemplatePreview
                  templateId={previewTemplateId}
                  variant="inline"
                  scaleToWidth
                  enabled
                  refreshKey={previewRefreshKey}
                />
              ) : (
                <p className={styles.applyTemplateSidePreviewEmpty}>
                  Select a template to preview.
                </p>
              )}
            </div>
          </aside>
        </div>
      </ConfirmationDialog>
  )
}

export default InviteEmailDesignApplyTemplateModal
